import { Injectable } from '@angular/core';

import { AnalyticsEvent } from '../../Domain/Model/analytics.event';
import { ApiErrorEvent } from '../../Domain/Model/Events/System/api-error.event';
import { AppLaunchEvent } from '../../Domain/Model/Events/System/app-launch.event';
import { ErrorOccurredEvent } from '../../Domain/Model/Events/System/error-occurred.event';
import { PerformanceEvent } from '../../Domain/Model/Events/System/performance.event';
import { ButtonClickEvent, ButtonClickEventContext } from '../../Domain/Model/Events/UserJourney/button-click.event';
import { NavigationEvent } from '../../Domain/Model/Events/UserJourney/navigation.event';
import { UserLoginEvent } from '../../Domain/Model/Events/UserJourney/user-login.event';
import { AnalyticsService } from '../../Domain/Port/analytics.service';
import { EventRepository } from '../../Domain/Port/event-repository';

/**
 * The service relies on an instance of EventRepository to persist events.
 */
@Injectable({
    providedIn: 'root'
})
export class EventRepositoryAnalyticsService extends AnalyticsService {
    constructor(private readonly eventRepository: EventRepository) {
        super();
    }

    /**
     * Logs a custom analytics event using the underlying event repository.
     *
     * @param event - The analytics event to log
     * @returns A promise that resolves when the event is logged
     */
    override async logCustomEvent(event: AnalyticsEvent): Promise<void> {
        await this.eventRepository.logEvent(event);
    }

    /**
     * Logs a button click event.
     * Creates a ButtonClickEvent and logs it using logCustomEvent.
     *
     * @param buttonId - The identifier of the clicked button
     * @param form - The form or screen where the button was clicked
     * @param context - Optional additional context about the button click
     * @returns A promise that resolves when the event is logged
     */
    async logButtonClick(buttonId: string, form: string, context?: ButtonClickEventContext): Promise<void> {
        const event = new ButtonClickEvent(buttonId, form, context);
        await this.logCustomEvent(event);
    }

    /**
     * Logs a navigation event between screens or pages.
     * Creates a NavigationEvent and logs it using logCustomEvent.
     *
     * @param from - The source screen or page
     * @param to - The destination screen or page
     * @returns A promise that resolves when the event is logged
     */
    async logNavigation(from: string, to: string): Promise<void> {
        const event = new NavigationEvent(from, to);
        await this.logCustomEvent(event);
    }

    /**
     * Logs an application launch event.
     * Creates an AppLaunchEvent and logs it using logCustomEvent.
     *
     * @returns A promise that resolves when the event is logged
     */
    async logAppLaunch(): Promise<void> {
        const event = new AppLaunchEvent();
        await this.logCustomEvent(event);
    }

    /**
     * Logs a user login event.
     * Creates a UserLoginEvent and logs it using logCustomEvent.
     *
     * @param method - The login method used (e.g., 'email', 'google', 'facebook')
     * @returns A promise that resolves when the event is logged
     */
    async logUserLogin(method: string): Promise<void> {
        const event = new UserLoginEvent(method);
        await this.logCustomEvent(event);
    }

    /**
     * Logs an error event.
     * Creates an ErrorOccurredEvent and logs it using logCustomEvent.
     *
     * @param errorType - The type or category of the error
     * @param errorMessage - The error message or description
     * @returns A promise that resolves when the event is logged
     */
    async logError(errorType: string, errorMessage: string): Promise<void> {
        const event = new ErrorOccurredEvent(errorType, errorMessage);
        await this.logCustomEvent(event);
    }

    /**
     * Logs a performance event with duration calculation.
     * Creates a PerformanceEvent with the calculated duration and logs it using logCustomEvent.
     *
     * @param action - The action or operation being measured
     * @param startTime - The timestamp when the action started (in milliseconds)
     * @param success - Whether the action completed successfully (defaults to true)
     * @returns A promise that resolves when the event is logged
     */
    async logPerformance(action: string, startTime: number, success: boolean = true): Promise<void> {
        const duration = Date.now() - startTime;
        const event = new PerformanceEvent(action, duration, success);
        await this.logCustomEvent(event);
    }

    /**
     * Logs an API error event with sanitized request and response data.
     * Sanitizes the request and response data to remove sensitive information,
     * creates an ApiErrorEvent, and logs it using logCustomEvent.
     *
     * @param endpoint - The API endpoint that returned the error
     * @param statusCode - The HTTP status code of the error
     * @param request - The request data that caused the error
     * @param response - The error response data
     * @returns A promise that resolves when the event is logged
     */
    async logApiError(
        endpoint: string,
        statusCode: number,
        request: Record<string, unknown>,
        response: Record<string, unknown>
    ): Promise<void> {
        const sanitizedRequest = this.sanitizeData(request);
        const sanitizedResponse = this.sanitizeData(response);
        const event = new ApiErrorEvent(endpoint, statusCode, sanitizedRequest, sanitizedResponse);
        await this.logCustomEvent(event);
    }

    /**
     * Sanitizes data by redacting sensitive information.
     * Creates a copy of the input data and replaces values for sensitive keys with '[REDACTED]'.
     *
     * @param data - The data object to sanitize
     * @returns A sanitized copy of the data with sensitive information redacted
     */
    private sanitizeData(data: Record<string, unknown>): Record<string, unknown> {
        const sensitiveKeys = ['userId', 'token', 'email', 'password', 'pwd', 'user-id'];
        const sanitized = { ...data };
        sensitiveKeys.forEach(key => {
            if (sanitized[key]) sanitized[key] = '[REDACTED]';
        });
        return sanitized;
    }
}
